import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountLayout from "../blocs/AccountLayout";
import Table from "../components/Table";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInvoiceByRdvId,
  fetchRendezvous,
  updateRendezVous,
} from "../store/slices/rendezVousSlice";
import Popup from "../components/Popup";
import {
  formatDecimalHour,
  formatDuration,
  isLessThan48h,
  StatusRdv,
} from "../utils/utils";
import TooltipTwyny from "../components/tooltip";
import RendezVousSkeleton from "../components/skeletons/RendezVousSkeleton";
import { toast } from "react-toastify";

const Rendezvous = () => {
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [selectedRdvId, setSelectedRdvId] = useState(null);
  const [selectedRdvStartTime, setSelectedRdvStartTime] = useState(null);
  const [selectedRdvStatus, setSelectedRdvStatus] = useState(null);
  const [popupActionType, setPopupActionType] = useState(null); // 'delete' ou 'edit'

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { rendezvous, status, error } = useSelector(
    (state) => state.rendezvous
  );
  // console.log("rendezvous client:", rendezvous);
  useEffect(() => {
    dispatch(fetchRendezvous());
  }, [dispatch]);

  const handlePopupAction = () => {
    if (popupActionType === "delete") {
      const updatedRdv = {
        id: selectedRdvId,
        status: "annule",
      };

      dispatch(updateRendezVous(updatedRdv))
        .unwrap()
        .then(() => {
          setPopupVisible(false);
          setSelectedRdvId(null);
          toast.success("Rendez-vous annulé avec succès.");
          dispatch(fetchRendezvous());
        })
        .catch((error) => {
          // console.error("Erreur lors de l'annulation :", error);
        });
    } else if (popupActionType === "edit") {
      setPopupVisible(false);
      navigate(`/compte/rendez-vous/edit/${selectedRdvId}`);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Date de réservation",
        accessor: "reservationDate",
      },
      {
        Header: "Statut",
        accessor: "statut",
        Cell: ({ value }) => {
          const statusIcons = StatusRdv();
          return (
            <span className={`icon-status ${value}`}>
              <img src={statusIcons[value]} alt={value} />
            </span>
          );
        },
      },
      {
        Header: "Pro",
        accessor: "pro",
      },
      {
        Header: "Lieu",
        accessor: "location",
        width: 200,
      },
      {
        Header: "Prestation",
        accessor: "service",
        // width: 200
      },
      {
        Header: "Temps",
        accessor: "time",
      },
      {
        Header: "Total TTC",
        accessor: "totalTTC",
      },
      {
        Header: "Action",
         className: "sticky-action",
        Cell: ({ row }) => {
          const {
            id,
            statut,
            start_time,
            prestation_ids,
            has_review,
            has_refund_request,
            refunds,
          } = row.original;
          const handleRowClick = () => {
            navigate(`/compte/rendez-vous/${id}`);
          };

          const handleEditClick = (e) => {
            e.stopPropagation();
            if (row?.original?.statut == "enattente") {
              navigate(`/compte/rendez-vous/edit/${id}`);
            } else {
              if (isLessThan48h(start_time)) {
                setSelectedRdvId(id);
                setSelectedRdvStartTime(start_time);
                setPopupActionType("edit");
                setPopupVisible(true);
              } else {
                navigate(`/compte/rendez-vous/edit/${id}`);
              }
            }
          };

          const handleDeleteClick = (e) => {
            e.stopPropagation();
            setSelectedRdvId(id);
            setSelectedRdvStartTime(start_time);
            setSelectedRdvStatus(statut);
            setPopupActionType("delete");
            setPopupVisible(true);
          };

          const handleDownloadClick = async (e) => {
            e.stopPropagation();
            try {
              const url = await dispatch(
                fetchInvoiceByRdvId(row.original.id)
              ).unwrap();
              // console.log("URL reçue depuis le thunk :", url);
              if (url) window.open(url, "_blank");
            } catch (err) {
              // console.error("Erreur de récupération :", err);
            }
          };

          const handleReimbursementClick = (e) => {
            e.stopPropagation();
            navigate(`/compte/demande-remboursement/${id}`);
          };

          const handleCommentClick = (e) => {
            e.stopPropagation();
            navigate(`/compte/review/${id}`);
          };

          return (
            <div className="action-icons" onClick={handleRowClick}>
              {statut === "enattente" && (
                <>
                  <button onClick={handleEditClick} className="action-btn edit">
                    <TooltipTwyny
                      children={<img src="/images/edit.svg" alt="edit icon" />}
                      className="left100"
                      text="Reprogrammer le rendez-vous"
                    />
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="action-btn delete"
                  >
                    <TooltipTwyny
                      children={
                        <img src="/images/delete.svg" alt="delete icon" />
                      }
                      className="left100"
                      text="Annuler le rendez-vous"
                    />
                  </button>
                </>
              )}
              {statut === "confirme" && (
                <>
                  <button
                    onClick={handleDownloadClick}
                    className="action-btn download"
                  >
                    <TooltipTwyny
                      className="left100"
                      children={
                        <img
                          src="/images/download.svg"
                          alt="information icon"
                        />
                      }
                      text="Télécharger ma facture"
                    />
                  </button>
                  <button onClick={handleEditClick} className="action-btn edit">
                    <TooltipTwyny
                      className="left100"
                      children={<img src="/images/edit.svg" alt="edit icon" />}
                      text="Reprogrammer le rendez-vous"
                    />
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="action-btn delete"
                  >
                    <TooltipTwyny
                      className="left100"
                      children={<img src="/images/delete.svg" alt="Delete" />}
                      text="Annuler le rendez-vous"
                    />
                  </button>
                </>
              )}
              {statut === "passe" && (
                <>
                  {refunds?.[0]?.stripe_receipt_url && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = refunds[0]?.stripe_receipt_url;
                        // console.log("here", refunds);
                        if (url) {
                          const link = document.createElement("a");
                          link.href = url;
                          link.target = "_blank";
                          link.rel = "noopener noreferrer"; // Bonne pratique de sécurité
                          document.body.appendChild(link); // Ajout au DOM
                          link.click(); // Déclenchement du clic
                          document.body.removeChild(link); // Nettoyage
                        }
                      }}
                      className="action-btn download"
                    >
                      <TooltipTwyny
                        className="left100"
                        children={
                          <img src="/images/download.svg" alt="download" />
                        }
                        text="Facture d'avoir"
                      />
                    </button>

                  )}

                  <button
                    onClick={handleDownloadClick}
                    className="action-btn download"
                  >
                    <TooltipTwyny
                      className="left100"
                      children={
                        <img src="/images/download.svg" alt="download" />
                      }
                      text="Télécharger ma facture"
                    />
                  </button>
                  {has_refund_request === true ? (
                    <button
                      onClick={handleReimbursementClick}
                      className="action-btn reimbursement disabled"
                      disabled
                      aria-disabled="true"
                      tabIndex={-1}
                    >
                      <TooltipTwyny
                        children={
                          <img
                            src="/images/rembourse.svg"
                            alt="remboursement icon"
                          />
                        }
                        className="left100"
                        text="Vous avez déjà envoyé une demande de remboursement"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={handleReimbursementClick}
                      className="action-btn reimbursement"
                    >
                      <TooltipTwyny
                        children={
                          <img
                            src="/images/rembourse.svg"
                            alt="remboursement icon"
                          />
                        }
                        className="left100"
                        text="Demander le Remboursement"
                      />
                    </button>
                  )}

                  {has_review === true ? (
                    <button
                      className="action-btn comment disabled"
                      disabled
                      aria-disabled="true"
                      tabIndex={-1}
                    >
                      <TooltipTwyny
                        children={
                          <img src="/images/comment.svg" alt="Comment" />
                        }
                        text="Vous avez déjà noté ce professionnel"
                        className="left100"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={handleCommentClick}
                      className="action-btn comment"
                    >
                      <TooltipTwyny
                        children={
                          <img src="/images/comment.svg" alt="Comment" />
                        }
                        text="Notez votre professionnel"
                        className="left100"
                      />
                    </button>
                  )}
                </>
              )}
              {statut === "annule" && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = refunds[0]?.stripe_receipt_url;
                      // console.log("here", url);
                      if (url) {
                        const link = document.createElement("a");
                        link.href = url;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer"; // Bonne pratique de sécurité
                        document.body.appendChild(link); // Ajout au DOM
                        link.click(); // Déclenchement du clic
                        document.body.removeChild(link); // Nettoyage
                      }
                    }}
                    className="action-btn download"
                  >
                    <TooltipTwyny
                      className="left100"
                      children={
                        <img src="/images/download.svg" alt="download" />
                      }
                      text="Facture d'avoir"
                    />
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  const pagination = {
    canPreviousPage: !!rendezvous?.data?.prev_page_url,
    canNextPage: !!rendezvous?.data?.next_page_url,
    previousPageUrl: rendezvous?.data?.prev_page_url || null,
    nextPageUrl: rendezvous?.data?.next_page_url || null,
  };

  const handlePageChange = (url) => {
    if (url) {
      dispatch(fetchRendezvous({ url }));
    }
  };

  const data = React.useMemo(() => {
    const appointments = rendezvous?.data?.data || [];
    if (!Array.isArray(appointments)) return [];
    // console.log("appt", appointments);
    return appointments.map((appt) => ({
      id: appt?.id,
      prestation_ids: appt?.prestation_ids,
      reservationDate:
        new Date(appt?.start_time).toLocaleDateString() +
        "\n" +
        new Date(appt?.start_time).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        //  timeZone: "Europe/Paris",
        }),
      statut: appt?.status,
      pro: (
        <a
          href={`/etablissement/${appt?.etablissement?.id}`}
          className="body grey66"
        >
          {appt?.etablissement?.nom}
        </a>
      ),
      location:
        appt?.lieu_intervention === "enligne"
          ? appt?.meeting_link || "En ligne"
          : appt?.lieu_intervention === "onsite"
            ? appt?.adresse_domicile
            : appt?.lieu_intervention === "chezpro"
              ? appt?.etablissement?.code_postal +
              " " +
              appt?.etablissement?.ville +
              " " +
              appt?.etablissement?.adresse_postale
              : "N/A",
      service: appt?.prestations_snapshot?.[0]?.name || "N/A",
      time: formatDecimalHour(appt?.duration_hours),
      totalTTC: `${appt?.amount_total == null ? appt?.total_ttc : appt?.amount_total}€`,
      start_time: appt?.start_time,
      has_review: appt?.has_review,
      has_refund_request: appt?.has_refund_request,
      refunds: appt?.refunds,
    }));
  }, [rendezvous]);

  return (
    <>
      <AccountLayout bgColor={"bgColor"}>
        <div className="section-title">
          <h3>Mes rendez-vous</h3>
        </div>
        {status === "loading" ? (
          <RendezVousSkeleton />
        ) : (
          <>
            {data?.length == 0 ? (
              <></>
            ) : (
              <>
                <p className="body grey66 pd-bt-24">
                  Attention, seules les annulations de rendez-vous plus de 48h
                  avant la date du rendez-vous vous permettront d’être
                  remboursé.
                </p>
                <Table
                  columns={columns}
                  data={data}
                  onRowClick={"true"}
                  path={"/compte/rendez-vous"}
                  previousPage={() =>
                    handlePageChange(pagination.previousPageUrl)
                  }
                  nextPage={() => handlePageChange(pagination.nextPageUrl)}
                />
              </>
            )}
          </>
        )}
      </AccountLayout>

      {isPopupVisible && (
        <Popup
          isOpen={isPopupVisible}
          onClose={() => {
            setPopupVisible(false);
            setSelectedRdvId(null);
            setSelectedRdvStartTime(null);
            setSelectedRdvStatus(null);
          }}
          onAction={handlePopupAction}
          title={
            popupActionType === "edit"
              ? "Modification impossible à ce stade"
              : isLessThan48h(selectedRdvStartTime) &&
                selectedRdvStatus === "confirme"
                ? "Information importante concernant votre annulation"
                : "Annuler le rendez-vous"
          }
          text={
            popupActionType === "delete" ? (
              selectedRdvStatus === "enattente" ? (
                <p className="body">
                  Votre demande est toujours en attente de confirmation par le
                  prestataire.
                  <br />
                  Conformément à nos conditions, vous êtes éligible à un
                  remboursement. Nous procéderons à l’annulation de votre
                  réservation.
                </p>
              ) : selectedRdvStatus === "confirme" &&
                isLessThan48h(selectedRdvStartTime) ? (
                <p className="body">
                  Votre réservation commence dans moins de 48 heures. <br />
                  Toute annulation à ce stade n’est pas remboursable, comme
                  indiqué dans nos conditions. <br />
                  Nous restons disponibles pour toute question.
                </p>
              ) : selectedRdvStatus === "confirme" ? (
                <p className="body">
                  Votre demande concerne une réservation prévue dans plus de 48
                  heures. <br />
                  Conformément à nos conditions, vous êtes éligible à un
                  remboursement partiel. <br />
                  Nous procéderons à l’annulation de votre réservation.
                </p>
              ) : null
            ) : (
              <p className="body">
                Conformément à nos conditions, les modifications de date ou
                d’horaire ne sont plus possibles dans ce délai. Si vous avez une
                urgence, n’hésitez pas à nous contacter – nous ferons de notre
                mieux pour vous accompagner.
              </p>
            )
          }
          actionButtonText={
            popupActionType === "edit" && isLessThan48h(selectedRdvStartTime)
              ? undefined
              : popupActionType === "edit"
                ? "Modifier le rendez-vous"
                : "Annuler mon rendez-vous"
          }
          closeButtonText="Revenir"
          closeButtonClassName="bold600"
          color="blue"
          icon={
            <svg
              width="88"
              height="88"
              viewBox="0 0 88 88"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="88"
                height="88"
                rx="14.6667"
                fill="#DD1E03"
                fillOpacity="0.1"
              />
              <path
                d="M44.4001 44.4001V34.5001M44.4001 51.7381V51.8251M64.2001 44.4001C64.2001 55.3353 55.3353 64.2001 44.4001 64.2001C33.4649 64.2001 24.6001 55.3353 24.6001 44.4001C24.6001 33.4649 33.4649 24.6001 44.4001 24.6001C55.3353 24.6001 64.2001 33.4649 64.2001 44.4001Z"
                stroke="#DD1E03"
                strokeWidth="4.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
      )}
    </>
  );
};

export default Rendezvous;
